import { SummaryData } from './types';
import { getAppInstance } from '@/utils/api/app';
import { withTtlCache } from '@/utils/cache/withTtlCache';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { getTrustpilotClient } from '@/utils/api/trustpilot';

// The Trustpilot summary (score + review count) changes slowly, so cache it
// in-process for a few minutes. Every page render fetches it, and the Trustpilot
// API rate-limits — without this each pod would call the upstream on every SSR
// and every /api/trustpilot/summary hit.
const TRUSTPILOT_SUMMARY_TTL_MS = 5 * 60 * 1000;

/**
 * React query key
 *
 * @var {String}
 */
export const queryKey: QueryKey = ['trustpilot', 'summary'];

/**
 * Get Trustpilot summary using the client
 *
 * @return  {SummaryData}  Trustpilot summary data
 */
const clientTrustpilotSummary = async () => {
  const { data } = await getAppInstance().post<SummaryData>('/trustpilot/summary');

  return data;
};

/**
 * Get Trustpilot summary using the server
 *
 * @return  {SummaryData}  Trustpilot summary data
 */
export const serverTrustpilotSummary = withTtlCache(async () => {
  const { data } = await getTrustpilotClient().get<SummaryData>(`/business-units/4c9328ce00006400050de080/`);

  return data;
}, TRUSTPILOT_SUMMARY_TTL_MS);

/**
 * Trustpilot Summary Query
 *
 * @return  {UseQueryResult<SummaryData, unknown>}  React Query return type
 */
export function useTrustpilotSummary() {
  return useQuery({
    queryFn: clientTrustpilotSummary,
    queryKey,
  });
}
